// src/components/CircuitSummary.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getUserLaps, getCircuit } from '../utils/firestoreUtils';
import { Container } from '@mui/material';
import { Link } from 'react-router-dom';

const formatLapTime = (time) => {
  if (!time) return '';
  const s = parseFloat(time);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s % 1) * 1000);
  if (m === 0) return `${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

const PODIUM = ['var(--gold)', 'var(--silver)', 'var(--bronze)'];
const PODIUM_LABEL = ['1ST', '2ND', '3RD'];

// Clickable lap time — links to proof if available
function LapTimeLink({ time, proof, style = {} }) {
  const formatted = formatLapTime(time);
  if (!formatted) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  if (proof) {
    return (
      <a
        href={proof}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          color: 'var(--accent)',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          ...style,
        }}
        title="View proof"
      >
        {formatted}
        <span style={{ fontSize: '0.7em', opacity: 0.7 }}>↗</span>
      </a>
    );
  }
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, ...style }}>
      {formatted}
    </span>
  );
}

function CircuitSummary() {
  const { circuitId } = useParams();
  const [circuit, setCircuit] = useState(null);
  const [laps, setLaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLayout, setSelectedLayout] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const circ = await getCircuit(circuitId);
        setCircuit(circ);
        if (circ?.layouts?.length > 0) setSelectedLayout(circ.layouts[0].layoutName);

        const usersSnapshot = await getDocs(collection(db, 'users'));
        const allLaps = [];
        await Promise.all(usersSnapshot.docs.map(async (userDoc) => {
          const userId = userDoc.id;
          const userLaps = await getUserLaps(userId, true);
          const circuitLaps = userLaps.filter(lap => lap.track.startsWith(circuitId + '-'));
          allLaps.push(...circuitLaps.map(lap => ({
            ...lap,
            userId,
            userName: userDoc.data().displayName,
            layoutName: lap.track.split('-').slice(1).join('-'),
          })));
        }));

        allLaps.sort((a, b) => a.bestLap - b.bestLap);
        setLaps(allLaps);
      } catch (err) {
        console.error('Error fetching circuit summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [circuitId]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <div style={{ padding: '40px 0' }}>
          <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-xl)', marginBottom: 24 }} />
          <div className="skeleton skeleton-row" style={{ marginBottom: 8 }} />
          <div className="skeleton skeleton-row" style={{ marginBottom: 8, opacity: 0.7 }} />
          <div className="skeleton skeleton-row" style={{ opacity: 0.5 }} />
        </div>
      </Container>
    );
  }

  if (!circuit) return <Container maxWidth="lg"><p style={{ color: 'var(--text-tertiary)', marginTop: 40 }}>Circuit not found.</p></Container>;

  const layouts = circuit.layouts || [];
  const activeLayout = layouts.find(l => l.layoutName === selectedLayout) || layouts[0];
  const filteredLaps = selectedLayout
    ? laps.filter(l => l.layoutName === selectedLayout)
    : laps;

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>

      {/* ── HERO HEADER ── */}
      <div style={{
        background: 'var(--text-primary)',
        borderRadius: 'var(--radius-xl)',
        padding: '36px 40px',
        marginBottom: 28,
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        flexWrap: 'wrap',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle glow */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 70% 50%, rgba(25,130,206,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />

        {/* Track map */}
        {activeLayout?.map && (
          <div style={{
            position: 'relative',
            width: 140,
            height: 140,
            flexShrink: 0,
            background: '#fff',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 10,
          }}>
            <img
              src={activeLayout.map}
              alt={`${circuit.name} track map`}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        )}

        {/* Text info */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(25,130,206,0.85)',
            color: '#fff',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '3px 10px',
            borderRadius: 'var(--radius-pill)',
            marginBottom: 12,
          }}>
            {circuit.country || 'Circuit'}
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
            color: '#fff',
            lineHeight: 1,
            letterSpacing: '0.03em',
            marginBottom: 16,
            fontWeight: 400,
          }}>
            {circuit.name}
          </h1>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {activeLayout?.length && (
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', color: '#fff', fontWeight: 600, lineHeight: 1 }}>
                  {activeLayout.length}<span style={{ fontSize: '0.75rem', marginLeft: 4, color: 'rgba(255,255,255,0.6)' }}>km</span>
                </div>
                <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                  Track Length
                </div>
              </div>
            )}
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', color: '#fff', fontWeight: 600, lineHeight: 1 }}>
                {filteredLaps.length}
              </div>
              <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                Laps Recorded
              </div>
            </div>
            {filteredLaps[0] && (
              <div>
                <div style={{ lineHeight: 1 }}>
                  <LapTimeLink time={filteredLaps[0].bestLap} proof={filteredLaps[0].proof} style={{ fontSize: '1.3rem', color: 'var(--gold)' }} />
                </div>
                <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                  Track Record
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Layout tabs (if multiple) */}
      {layouts.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {layouts.map(lay => (
            <button
              key={lay.layoutName}
              onClick={() => setSelectedLayout(lay.layoutName)}
              style={{
                padding: '7px 16px',
                borderRadius: 'var(--radius-pill)',
                border: selectedLayout === lay.layoutName ? 'none' : '1px solid var(--border-strong)',
                background: selectedLayout === lay.layoutName ? 'var(--accent)' : 'var(--surface)',
                color: selectedLayout === lay.layoutName ? '#fff' : 'var(--text-secondary)',
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
                fontSize: '0.82rem',
                cursor: 'pointer',
                transition: 'all var(--transition)',
              }}
            >
              {lay.layoutName}
              {lay.length && <span style={{ opacity: 0.7, fontWeight: 400, marginLeft: 4 }}>{lay.length}km</span>}
            </button>
          ))}
        </div>
      )}

      {/* ── TOP 3 PODIUM ── */}
      {filteredLaps.length > 0 && (
        <>
          <h2 className="section-heading" style={{ marginTop: 0 }}>Top Times</h2>
          <div className="podium-section" style={{ marginBottom: 28 }}>
            {[1, 0, 2].map(idx => {
              const lap = filteredLaps[idx];
              return (
                <div key={idx} className={`podium-card p${idx + 1}`}>
                  <div className="podium-rank">{PODIUM_LABEL[idx]}</div>
                  {lap ? (
                    <>
                      <div className="podium-name">
                        <Link to={`/users/${lap.userId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {lap.userName}
                        </Link>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <LapTimeLink time={lap.bestLap} proof={lap.proof} style={{ fontSize: '1.1rem', color: PODIUM[idx] }} />
                      </div>
                      {lap.eventName && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 6 }}>{lap.eventName}</div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>—</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── FULL LEADERBOARD TABLE ── */}
      {filteredLaps.length > 0 && (
        <div className="leaderboard-wrap">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>Driver</th>
                <th>Layout</th>
                <th>Event</th>
                <th>Tires</th>
                <th style={{ textAlign: 'right' }}>Time</th>
                <th style={{ textAlign: 'right' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {filteredLaps.map((lap, i) => (
                <tr key={lap.id}>
                  <td className="rank-cell">
                    {i < 3 ? (
                      <span className={`rank-badge rank-${i + 1}`}>{i + 1}</span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{i + 1}</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/users/${lap.userId}`} className="user-link">
                      <span className="user-avatar-sm">
                        {(lap.userName || '?')[0].toUpperCase()}
                      </span>
                      {lap.userName}
                    </Link>
                  </td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{lap.layoutName || '—'}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{lap.eventName || '—'}</td>
                  <td>
                    {lap.tireModel ? (
                      <span style={{
                        display: 'inline-block',
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-pill)',
                        padding: '2px 9px',
                        fontSize: '0.75rem',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        whiteSpace: 'nowrap',
                      }}>
                        {lap.tireModel}
                      </span>
                    ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <LapTimeLink time={lap.bestLap} proof={lap.proof} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={`points-badge ${!lap.points ? 'zero' : ''}`}>{lap.points || 0}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredLaps.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-tertiary)' }}>
          No approved laps recorded for this circuit yet.
        </div>
      )}
    </Container>
  );
}

export default CircuitSummary;
